
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Eye, EyeOff } from 'lucide-react';
import { UserRole } from './UserTable';

interface AddUserDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddUserDialog = ({ isOpen, onOpenChange }: AddUserDialogProps) => {
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'user'>('user');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Add user mutation
  const addUserMutation = useMutation({
    mutationFn: async ({ email, role, password }: { email: string; role: 'admin' | 'user'; password: string }) => {
      console.log('Creating new user via Supabase Auth:', email);
      
      try {
        // Create user via Supabase Auth
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              role: role
            }
          }
        });

        if (signUpError) {
          console.error('Error creating auth user:', signUpError);
          throw new Error(signUpError.message);
        }

        if (!authData.user) {
          throw new Error('Failed to create user');
        }

        console.log('Auth user created:', authData.user.id);

        // Check if user_roles entry already exists (from trigger)
        const { data: existingRole } = await supabase
          .from('user_roles')
          .select('*')
          .eq('user_email', email)
          .maybeSingle();

        // If role entry exists but needs update, update it
        if (existingRole && existingRole.role !== role) {
          const { error: updateError } = await supabase
            .from('user_roles')
            .update({ role, user_id: authData.user.id })
            .eq('user_email', email);

          if (updateError) {
            console.error('Error updating user role:', updateError);
            throw new Error('User created but role assignment failed');
          }
        } else if (!existingRole) {
          // If no role entry, create one
          const { error: insertError } = await supabase
            .from('user_roles')
            .insert([{ 
              user_email: email, 
              role,
              user_id: authData.user.id
            }]);

          if (insertError) {
            console.error('Error inserting user role:', insertError);
            throw new Error('User created but role assignment failed');
          }
        }

        console.log('Successfully created user with role');
        return authData.user;
      } catch (error) {
        console.error('Exception while creating user:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles'] });
      toast({
        title: "Success",
        description: "User added successfully",
      });
      setNewUserEmail('');
      setNewUserRole('user');
      setNewUserPassword('');
      onOpenChange(false);
      setIsSubmitting(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setIsSubmitting(false);
    },
  });

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newUserEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    if (!newUserPassword) {
      toast({
        title: "Error",
        description: "Please enter a password",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    addUserMutation.mutate({ 
      email: newUserEmail, 
      role: newUserRole,
      password: newUserPassword
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>
            Enter the email address, password, and role for the new user. The user will receive a confirmation email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleAddUser} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                required
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select 
              value={newUserRole} 
              onValueChange={(value: 'admin' | 'user') => setNewUserRole(value)}
            >
              <SelectTrigger id="role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
